import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useAdminUsers, adminApi } from '@hooks/useAdminApi';
import { toastManager } from './ToastContainer';
import Modal from './Modal';

function UserManager() {
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [page, setPage] = useState(1);
    const [selectedUser, setSelectedUser] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const searchTimeoutRef = useRef(null);
    
    const { users, pagination, isLoading, mutate } = useAdminUsers(search, statusFilter, page);
    
    // Debounce search input
    useEffect(() => {
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }
        
        searchTimeoutRef.current = setTimeout(() => {
            setSearch(searchInput);
            setPage(1);
        }, 500); // 500ms debounce
        
        return () => {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
        };
    }, [searchInput]);
    
    const handleSearch = useCallback((e) => {
        setSearchInput(e.target.value);
    }, []);
    
    const handleStatusFilter = useCallback((e) => {
        setStatusFilter(e.target.value);
        setPage(1);
    }, []);
    
    const handleViewUser = async (userId) => {
        try {
            const result = await adminApi.getUser(userId);
            if (result.success) {
                setSelectedUser(result.user);
                setIsModalOpen(true);
            }
        } catch (error) {
            toastManager.error('Error', 'Failed to load user details');
        }
    };
    
    const handleBanUser = async (userId, email) => {
        if (!confirm(`Are you sure you want to ban ${email}?`)) return;
        
        try {
            const result = await adminApi.updateUser(userId, { is_banned: true });
            if (result.success) {
                toastManager.success('User Banned', `${email} has been banned`);
                mutate(); // Refresh list
            } else {
                throw new Error(result.message);
            }
        } catch (error) {
            toastManager.error('Error', error.message || 'Failed to ban user');
        }
    };
    
    const handleUnbanUser = async (userId, email) => {
        try {
            const result = await adminApi.updateUser(userId, { is_banned: false });
            if (result.success) {
                toastManager.success('User Unbanned', `${email} has been unbanned`);
                mutate();
            } else {
                throw new Error(result.message);
            }
        } catch (error) {
            toastManager.error('Error', error.message || 'Failed to unban user');
        }
    };

    const handleResetPassword = async (userId, email) => {
        const newPassword = prompt(`Reset password untuk ${email}:\n\nMasukkan password baru (min 6 karakter):`);
        
        if (!newPassword) return;
        
        if (newPassword.length < 6) {
            toastManager.error('Error', 'Password minimal 6 karakter');
            return;
        }

        if (!confirm(`Yakin ingin reset password untuk ${email}?`)) return;
        
        try {
            const result = await adminApi.resetUserPassword(userId, newPassword);
            if (result.success) {
                toastManager.success('Success', `Password untuk ${email} berhasil direset`);
            } else {
                throw new Error(result.message);
            }
        } catch (error) {
            toastManager.error('Error', error.message || 'Gagal reset password');
        }
    };

    const handleAdjustVIP = async (userId, email, currentStatus, currentExpiry) => {
        if (currentStatus === 'vip_permanent') {
            toastManager.error('Error', 'User adalah VIP permanent, tidak bisa adjust expiration');
            return;
        }

        const days = prompt(`Adjust masa VIP untuk ${email}:\n\n+7 = tambah 7 hari\n-7 = kurangi 7 hari\n\nMasukkan jumlah hari:`);
        
        if (!days) return;
        
        const adjustDays = parseInt(days);
        if (isNaN(adjustDays) || adjustDays === 0) {
            toastManager.error('Error', 'Masukkan angka yang valid (tidak boleh 0)');
            return;
        }

        if (!confirm(`${adjustDays > 0 ? 'Tambah' : 'Kurangi'} ${Math.abs(adjustDays)} hari VIP untuk ${email}?`)) return;
        
        try {
            const result = await adminApi.adjustUserVIP(userId, adjustDays);
            if (result.success) {
                toastManager.success('Success', result.message);
                mutate(); // Refresh list
            } else {
                throw new Error(result.message);
            }
        } catch (error) {
            toastManager.error('Error', error.message || 'Gagal adjust VIP');
        }
    };
    
    const formatDate = useCallback((dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('id-ID', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    }, []);
    
    const getVIPBadge = useCallback((user) => {
        if (user.vip_status === 'vip_permanent') {
            return <span className="status-badge status-vip">💎 Permanent VIP</span>;
        } else if (user.vip_status === 'vip_temp' && user.vip_expires_at && new Date(user.vip_expires_at) > new Date()) {
            return <span className="status-badge status-vip">⏰ Temp VIP</span>;
        }
        return <span className="status-badge status-regular">👤 Regular</span>;
    }, []);
    
    // Memoize pagination controls
    const paginationControls = useMemo(() => {
        if (!pagination || pagination.totalPages <= 1) return null;
        
        return (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '25px', flexWrap: 'wrap', gap: '15px' }}>
                <div style={{ color: 'rgba(255,255,255,0.7)' }}>
                    Page {pagination.page} of {pagination.totalPages} 
                    ({pagination.total} total users)
                </div>
                <div style={{ display: 'flex', gap: '10px' }}>
                    <button 
                        className="admin-btn admin-btn-secondary"
                        onClick={() => setPage(p => Math.max(1, p - 1))}
                        disabled={page <= 1}
                    >
                        <i className="fas fa-chevron-left"></i> Previous
                    </button>
                    <button 
                        className="admin-btn admin-btn-secondary"
                        onClick={() => setPage(p => p + 1)}
                        disabled={page >= pagination.totalPages}
                    >
                        Next <i className="fas fa-chevron-right"></i>
                    </button>
                </div>
            </div>
        );
    }, [pagination, page]);
    
    return (
        <div className="admin-section">
            <h2 className="admin-section-title">
                <i className="fas fa-users"></i> User Management
            </h2>
            
            {/* Search and Filters */}
            <div style={{ display: 'flex', gap: '15px', marginBottom: '25px', flexWrap: 'wrap' }}>
                <input
                    type="text"
                    className="admin-input"
                    placeholder="Search by email or username..."
                    value={searchInput}
                    onChange={handleSearch}
                    style={{ flex: 1, minWidth: '250px' }}
                />
                <select 
                    className="admin-select" 
                    value={statusFilter} 
                    onChange={handleStatusFilter}
                    style={{ minWidth: '180px' }}
                >
                    <option value="">All Users</option>
                    <option value="active">✅ Active Only</option>
                    <option value="vip">👑 VIP Only</option>
                    <option value="regular">👤 Regular Only</option>
                    <option value="banned">🚫 Banned Only</option>
                </select>
                <button className="admin-btn admin-btn-secondary" onClick={() => mutate()}>
                    <i className="fas fa-sync-alt"></i> Refresh
                </button>
            </div>
            
            {/* Users Table */}
            {isLoading ? (
                <div className="admin-loading">
                    <i className="fas fa-spinner fa-spin"></i>
                    <p>Loading users...</p>
                </div>
            ) : users.length === 0 ? (
                <div className="admin-loading">
                    <i className="fas fa-users"></i>
                    <p>No users found</p>
                </div>
            ) : (
                <>
                    <div style={{ overflowX: 'auto' }}>
                        <table className="admin-table">
                            <thead>
                                <tr>
                                    <th>Email</th>
                                    <th>Username</th>
                                    <th>VIP Status</th>
                                    <th>Created</th>
                                    <th>Last Seen</th>
                                    <th>Status</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {users.map(user => (
                                    <tr key={user.id}>
                                        <td>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                <div style={{ 
                                                    width: '10px', 
                                                    height: '10px', 
                                                    borderRadius: '50%', 
                                                    background: user.is_online ? '#10b981' : '#6b7280',
                                                    boxShadow: user.is_online ? '0 0 8px rgba(16, 185, 129, 0.6)' : 'none',
                                                    border: user.is_online ? '2px solid #10b981' : '2px solid #6b7280'
                                                }}></div>
                                                {user.email}
                                            </div>
                                        </td>
                                        <td>{user.username || 'N/A'}</td>
                                        <td>{getVIPBadge(user)}</td>
                                        <td>{formatDate(user.created_at)}</td>
                                        <td>{(() => {
                                            const lastSeen = user.last_seen_at || user.last_login_at || user.created_at;
                                            if (!lastSeen) return 'Never';
                                            
                                            const now = new Date();
                                            const seenDate = new Date(lastSeen);
                                            const diffMs = now - seenDate;
                                            const diffMins = Math.floor(diffMs / 60000);
                                            const diffHours = Math.floor(diffMs / 3600000);
                                            const diffDays = Math.floor(diffMs / 86400000);
                                            
                                            if (diffMins < 1) return 'Just now';
                                            if (diffMins < 60) return `${diffMins}m ago`;
                                            if (diffHours < 24) return `${diffHours}h ago`;
                                            if (diffDays < 7) return `${diffDays}d ago`;
                                            return formatDate(lastSeen);
                                        })()}</td>
                                        <td>
                                            {user.is_banned ? (
                                                <span className="status-badge status-banned">
                                                    <i className="fas fa-ban"></i> Banned
                                                </span>
                                            ) : user.is_active ? (
                                                <span className="status-badge status-online">Active</span>
                                            ) : (
                                                <span className="status-badge status-offline">Inactive</span>
                                            )}
                                        </td>
                                        <td>
                                            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                                <button 
                                                    className="admin-btn admin-btn-sm admin-btn-secondary"
                                                    onClick={() => handleViewUser(user.id)}
                                                    title="View Details"
                                                >
                                                    <i className="fas fa-eye"></i>
                                                </button>
                                                <button 
                                                    className="admin-btn admin-btn-sm admin-btn-warning"
                                                    onClick={() => handleResetPassword(user.id, user.email)}
                                                    title="Reset Password"
                                                >
                                                    <i className="fas fa-key"></i>
                                                </button>
                                                {(user.vip_status === 'vip_temp' || user.vip_status === 'vip_permanent') && (
                                                    <button 
                                                        className="admin-btn admin-btn-sm admin-btn-info"
                                                        onClick={() => handleAdjustVIP(user.id, user.email, user.vip_status, user.vip_expires_at)}
                                                        title="Adjust VIP Time"
                                                        disabled={user.vip_status === 'vip_permanent'}
                                                    >
                                                        <i className="fas fa-calendar-alt"></i>
                                                    </button>
                                                )}
                                                {!user.is_banned ? (
                                                    <button 
                                                        className="admin-btn admin-btn-sm admin-btn-danger"
                                                        onClick={() => handleBanUser(user.id, user.email)}
                                                        title="Ban User"
                                                    >
                                                        <i className="fas fa-ban"></i>
                                                    </button>
                                                ) : (
                                                    <button 
                                                        className="admin-btn admin-btn-sm admin-btn-success"
                                                        onClick={() => handleUnbanUser(user.id, user.email)}
                                                        title="Unban User"
                                                    >
                                                        <i className="fas fa-check"></i>
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    
                    {/* Pagination */}
                    {paginationControls}
                </>
            )}
            
            {/* User Details Modal */}
            <Modal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                title="User Details"
                size="large"
            >
                {selectedUser && (
                    <div>
                        <div style={{ textAlign: 'center', marginBottom: '30px' }}>
                            <div style={{ 
                                width: '80px', 
                                height: '80px', 
                                borderRadius: '50%', 
                                background: 'linear-gradient(135deg, #d4af37, #f4d03f)', 
                                display: 'flex', 
                                alignItems: 'center', 
                                justifyContent: 'center', 
                                margin: '0 auto 15px', 
                                fontSize: '2rem', 
                                color: '#1a1a1e' 
                            }}>
                                <i className="fas fa-user"></i>
                            </div>
                            <h3 style={{ color: '#d4af37', margin: '0 0 5px 0' }}>{selectedUser.email}</h3>
                            <p style={{ color: 'rgba(255,255,255,0.6)', margin: 0 }}>@{selectedUser.username}</p>
                        </div>
                        
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px', marginBottom: '25px' }}>
                            <div style={{ background: 'rgba(255,255,255,0.05)', padding: '20px', borderRadius: '12px' }}>
                                <strong style={{ display: 'block', marginBottom: '8px', color: '#d4af37' }}>VIP Status:</strong>
                                {getVIPBadge(selectedUser)}
                            </div>
                            <div style={{ background: 'rgba(255,255,255,0.05)', padding: '20px', borderRadius: '12px' }}>
                                <strong style={{ display: 'block', marginBottom: '8px', color: '#d4af37' }}>Account Status:</strong>
                                {selectedUser.is_banned ? (
                                    <span className="status-badge status-banned">Banned</span>
                                ) : selectedUser.is_active ? (
                                    <span className="status-badge status-online">Active</span>
                                ) : (
                                    <span className="status-badge status-offline">Inactive</span>
                                )}
                            </div>
                            <div style={{ background: 'rgba(255,255,255,0.05)', padding: '20px', borderRadius: '12px' }}>
                                <strong style={{ display: 'block', marginBottom: '8px', color: '#d4af37' }}>Member Since:</strong>
                                <span style={{ color: 'rgba(255,255,255,0.8)' }}>{formatDate(selectedUser.created_at)}</span>
                            </div>
                            <div style={{ background: 'rgba(255,255,255,0.05)', padding: '20px', borderRadius: '12px' }}>
                                <strong style={{ display: 'block', marginBottom: '8px', color: '#d4af37' }}>Last Login:</strong>
                                <span style={{ color: 'rgba(255,255,255,0.8)' }}>{formatDate(selectedUser.last_login_at)}</span>
                            </div>
                        </div>
                        
                        <div style={{ display: 'flex', gap: '15px', justifyContent: 'center', marginTop: '30px' }}>
                            <button className="admin-btn admin-btn-secondary" onClick={() => setIsModalOpen(false)}>
                                Close
                            </button>
                        </div>
                    </div>
                )}
            </Modal>
        </div>
    );
}

export default UserManager;

